import { FilteredCreditApplication, PHONE_NUMBER_IFINANCE, PHONE_NUMBER_LINK_IFINANCE } from '@applyonline/shared'
import { EContractStatus, EWorksheetStatus } from '@if/core/types'
import { ReactNode, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useIsReportExpired } from '../../../../../containers/Worsheet/hooks'
import useRequiredDocumentsWithStatus from '../../../../../data/creditapp-hooks'
import { applicantFullName, areAllDocumentsReadyForCVT, hasMissingDocuments } from '../../../../../data/creditapp-selectors'
import { contractIsReceived } from '../../hooks'
import CreditStep, { CreditStepStatus } from './CreditStep'

interface Props {
  creditApp: FilteredCreditApplication
}

const WorksheetCreditStep = ({ creditApp }: Props) => {
  const { t } = useTranslation()
  const reportExpired = useIsReportExpired(creditApp.hardHitReportReceivedOn)
  const requiredDocumentsWithStatus = useRequiredDocumentsWithStatus(creditApp.requiredDocuments, creditApp.documents)

  const { status, content, link } = useMemo<{ status: CreditStepStatus; content?: ReactNode; link?: string }>(() => {
    if (hasMissingDocuments(creditApp)) return { status: CreditStepStatus.NotReady }

    if (reportExpired) {
      return {
        status: CreditStepStatus.RequiresAttention,
        link: creditApp.worksheet?.status === EWorksheetStatus.Draft ? 'worksheet' : 'contract',
        content: (
          <Trans
            i18nKey="overview.contract90Days"
            values={{
              phoneNumber: PHONE_NUMBER_IFINANCE,
            }}
            components={{
              a: <Link to={PHONE_NUMBER_LINK_IFINANCE} style={{ textWrap: 'nowrap', textDecoration: 'underline' }} />,
            }}
          />
        ),
      }
    }

    if (
      !creditApp.allIncomesConfirmed ||
      !creditApp.allTaskCompletedForCVT ||
      !areAllDocumentsReadyForCVT(requiredDocumentsWithStatus)
    )
      return { status: CreditStepStatus.Await, content: t('overview.worksheetContent') }

    if (contractIsReceived(creditApp)) {
      if (creditApp.contract.status === EContractStatus.Declined)
        return { status: CreditStepStatus.Await, content: t('contract.refused') }
      return { status: CreditStepStatus.Completed, content: `${t('contract.received')} ${t('contract.review')}` }
    }

    return {
      status: CreditStepStatus.RequiresAttention,
      content: t('overview.worksheetContent'),
      link: creditApp.worksheet?.status === EWorksheetStatus.Draft ? 'worksheet' : 'contract',
    }
  }, [creditApp, reportExpired, requiredDocumentsWithStatus, t])

  return (
    <CreditStep
      status={status}
      linkToDetail={link ?? ''}
      title={t('worksheet.title', { name: applicantFullName(creditApp.applicant) })}
      content={content ?? ''}
      hideLinkToDetail={status !== CreditStepStatus.RequiresAttention}
    />
  )
}

export default WorksheetCreditStep
